import BaseApi from "./base-api";
import {
  CreateReturnRequestPayload,
  ReturnRequest,
  ReturnsPagination,
} from "../types/ReturnRequest";

export const TERMINAL_RETURN_STATUSES = new Set([
  "rejected",
  "pickup_rejected",
  "refund_completed",
  "completed",
]);

export const isActiveReturnRequest = (status?: string | null) =>
  !!status && !TERMINAL_RETURN_STATUSES.has(status);

export const extractReturnPolicyDays = (payload: any, fallback = 7) => {
  const direct =
    payload?.return_policy_days ??
    payload?.returnPolicyDays ??
    payload?.data?.return_policy_days ??
    payload?.data?.returnPolicyDays;

  if (direct !== undefined && direct !== null && direct !== "") {
    const parsed = Number(direct);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  const settings = Array.isArray(payload?.data) ? payload.data : payload;
  if (Array.isArray(settings)) {
    const setting = settings.find(
      (item) =>
        item?.key === "return_policy_days" || item?.key === "returnPolicyDays",
    );
    const parsed = Number(setting?.value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  return fallback;
};

const extractUploadedUrl = (payload: any): string | null => {
  if (!payload) return null;
  if (typeof payload === "string") return payload;

  const candidates = [
    payload.url,
    payload.imageUrl,
    payload.mediaUrl,
    payload.location,
    payload.path,
    payload.data?.url,
    payload.data?.imageUrl,
    payload.data?.mediaUrl,
    payload.data?.location,
    payload.data?.path,
  ];

  const found = candidates.find((value) => typeof value === "string" && value);
  if (found) return found;

  if (Array.isArray(payload.urls) && payload.urls[0]) return payload.urls[0];
  if (Array.isArray(payload.data?.urls) && payload.data.urls[0]) {
    return payload.data.urls[0];
  }

  return null;
};

export const ReturnsApi = {
  create: (payload: CreateReturnRequestPayload) =>
    BaseApi.post("/returns", payload),

  list: async (
    page = 1,
    limit = 20,
    filters?: {
      status?: string;
      type?: string;
      startDate?: string;
      endDate?: string;
    },
  ): Promise<{
    success: boolean;
    data: ReturnRequest[];
    pagination?: ReturnsPagination;
    message?: string;
  }> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters?.status && filters.status !== "all") {
      params.append("status", filters.status);
    }
    if (filters?.type && filters.type !== "all") {
      params.append("type", filters.type);
    }
    if (filters?.startDate) {
      params.append("startDate", filters.startDate);
    }
    if (filters?.endDate) {
      params.append("endDate", filters.endDate);
    }

    return BaseApi.get(`/returns?${params.toString()}`);
  },

  listMine: async (
    page = 1,
    limit = 20,
  ): Promise<{
    success: boolean;
    data: ReturnRequest[];
    pagination?: ReturnsPagination;
    message?: string;
  }> => BaseApi.get(`/returns/my?page=${page}&limit=${limit}`),

  getById: async (
    id: string,
    silent = false,
  ): Promise<{ success: boolean; data?: ReturnRequest; message?: string }> =>
    BaseApi.get(`/returns/${id}`, {}, { skipLoader: silent }),

  review: (
    id: string,
    payload: { decision: "approved" | "rejected"; notes?: string },
  ) => BaseApi.patch(`/returns/${id}/review`, payload),

  assignDriver: (id: string, driverId: string) =>
    BaseApi.post(`/returns/${id}/assign-driver`, { driverId }),

  getAvailableDrivers: () => BaseApi.get("/delivery/drivers/available"),

  completePickup: (id: string) =>
    BaseApi.post(`/returns/${id}/pickup/complete`, { conditionOk: true }),

  completeExchange: (id: string) =>
    BaseApi.post(`/returns/${id}/exchange/complete`, { conditionOk: true }),

  rejectPickup: (id: string, reason: string) =>
    BaseApi.post(`/returns/${id}/pickup/reject`, { reason }),

  uploadMedia: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("media", file);

    const response = await BaseApi.post("/banners/upload", formData, {
      "Content-Type": "multipart/form-data",
    });
    const url = extractUploadedUrl(response);

    if (!url) {
      throw new Error(response?.message || "Failed to upload attachment");
    }

    return url;
  },

  fetchPolicyDays: async () => {
    const response = await BaseApi.get(
      "/settings/public",
      {},
      { skipLoader: true },
    );
    return extractReturnPolicyDays(response);
  },
};
